import React, { useEffect, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { getFirestore, collection, getDocs, getDoc, doc, setDoc, deleteDoc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import Swal from 'sweetalert2';
import { convertFactor } from './unitConverter';
import Select from 'react-select';
import AdminLayout from './AdminLayout';

/**
 * Use-units an ingredient line will deliver into inventory:
 * packages (qty) × content-per-package (unitsPerPackage, defaults 1) × the
 * unit conversion to the ingredient's use-unit. Conversion: the converter's
 * family table when the line's unit differs from the ingredient's purchase
 * unit; the ingredient's own conversionFactor when they match. Returns null
 * when the pair can't convert (the line must block the save).
 */
// 4-decimal rounding — kills float dust (63038.855500000005) at stock writes
const round4 = (v) => Math.round(v * 10000) / 10000;
const lineUseUnits = (line, ingredient) => {
  if (!ingredient) return null;
  const packages = parseFloat(line.qty) || 0;
  if (packages <= 0) return null;
  const perPackage = parseFloat(line.unitsPerPackage) || 1;
  const lineUnit = line.unit || ingredient.purchaseUnit || 'unit';
  const useUnit = ingredient.useUnit || 'unit';
  if (lineUnit === useUnit) return packages * perPackage;
  if (lineUnit === (ingredient.purchaseUnit || lineUnit)) {
    const f = Number(ingredient.conversionFactor) || 0;
    return f > 0 ? packages * perPackage * f : null;
  }
  const f = convertFactor(lineUnit, useUnit);
  return f == null ? null : packages * perPackage * f;
};

const money = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);
const EXPENSE_CATEGORIES = ['Inventory purchase', 'Rent', 'Utilities', 'Payroll', 'Equipment', 'Marketing', 'Fees', 'Supplies', 'Maintenance', 'Other'];
const LOSS_REASONS = ['Spoiled / expired', 'Burned / kitchen accident', 'Dropped / spilled', 'Theft / missing', 'Over-production', 'Other'];

/**
 * Expenses & Losses — money out beyond COGS (which the ingredient purchases
 * already feed). Receipts attach as files (Storage). Losses are a first-class
 * expense type (they hit the P&L like any cost) AND, when they reference an
 * ingredient, they also deplete its stock (the waste module of the spec).
 */
const ExpensesPage = () => {
  const [expenses, setExpenses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null); // draft being edited
  const [viewingEntry, setViewingEntry] = useState(null); // read-only detail
  const [receiveOffer, setReceiveOffer] = useState(null); // { ingredientLines, locationId } — post-save receive step
  const [formType, setFormType] = useState('expense'); // expense | loss
  const user = getAuth().currentUser;

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const db = getFirestore();
      const [expSnap, locSnap, ingSnap, supSnap] = await Promise.all([
        getDocs(query(collection(db, 'users', user.uid, 'expenses'), orderBy('date', 'desc'))),
        getDocs(collection(db, 'users', user.uid, 'locations')),
        getDocs(collection(db, 'users', user.uid, 'ingredients')),
        getDocs(collection(db, 'users', user.uid, 'suppliers')),
      ]);
      setExpenses(expSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLocations(locSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setIngredients(ingSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setSuppliers(supSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Final-sale products for purchase lines (ingredient | product)
      const catsSnap2 = await getDocs(collection(db, 'users', user.uid, 'categories'));
      const prods = [];
      for (const cat of catsSnap2.docs) {
        const prodSnap = await getDocs(collection(db, 'users', user.uid, 'categories', cat.id, 'products'));
        prodSnap.docs.forEach(pd => prods.push({ id: pd.id, categoryId: cat.id, ...pd.data() }));
      }
      setProducts(prods);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalExpenses = expenses.filter(e => e.type !== 'loss' && e.type !== 'return').reduce((a, e) => a + (e.amount || 0), 0);
  const totalLosses = expenses.filter(e => e.type === 'loss').reduce((a, e) => a + (e.amount || 0), 0);
  const totalReturns = expenses.filter(e => e.type === 'return').reduce((a, e) => a + (e.expectedRefund ?? e.amount ?? 0), 0);

  const handlePost = async (exp) => {
    const res = await Swal.fire({
      title: 'Post this entry?',
      html: 'Posted entries are <strong>accounting-final</strong> — they become read-only (no edits, no deletes).',
      icon: 'question', showCancelButton: true, confirmButtonColor: '#16a34a', confirmButtonText: 'Post',
    });
    if (!res.isConfirmed) return;
    const db = getFirestore();
    await setDoc(doc(db, 'users', user.uid, 'expenses', exp.id), {
      status: 'posted',
      postedAt: serverTimestamp(),
    }, { merge: true });
    await load();
  };

  const handleDelete = async (exp) => {
    if (exp.status === 'posted') {
      Swal.fire('Posted', 'Posted entries are read-only — they can no longer be deleted.', 'info');
      return;
    }
    const res = await Swal.fire({ title: 'Delete this entry?', icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33' });
    if (!res.isConfirmed) return;
    const db = getFirestore();
    await deleteDoc(doc(db, 'users', user.uid, 'expenses', exp.id));
    await load();
  };

  return (
    <AdminLayout title="Expenses & Losses">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="mb-0 font-weight-bold">Expenses & Losses</h5>
          <small className="text-muted">Money out + inventory waste — feeds the complete P&L</small>
        </div>
        <div>
          <button className="btn btn-outline-warning btn-sm mr-2" onClick={() => { setFormType('return'); setEditingEntry(null); setShowForm(true); }}>
            <i className="fas fa-rotate-left mr-1"></i> Log a return
          </button>
          <button className="btn btn-outline-danger btn-sm mr-2" onClick={() => { setFormType('loss'); setEditingEntry(null); setShowForm(true); }}>
            <i className="fas fa-fire mr-1"></i> Log a loss
          </button>
          <button className="btn btn-primary btn-sm" style={{ background: '#ff6b35', borderColor: '#ff6b35' }} onClick={() => { setFormType('expense'); setEditingEntry(null); setShowForm(true); }}>
            <i className="fas fa-plus mr-1"></i> New expense
          </button>
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-md-4">
          <div className="card"><div className="card-body py-3">
            <div className="text-muted small text-uppercase fw-bold">Expenses (all time)</div>
            <h4 className="fw-bold mb-0 mt-1">{money(totalExpenses)}</h4>
          </div></div>
        </div>
        <div className="col-md-4">
          <div className="card"><div className="card-body py-3">
            <div className="text-muted small text-uppercase fw-bold">Losses / waste (all time)</div>
            <h4 className="fw-bold mb-0 mt-1 text-danger">{money(totalLosses)}</h4>
          </div></div>
        </div>
        <div className="col-md-4">
          <div className="card"><div className="card-body py-3">
            <div className="text-muted small text-uppercase fw-bold">Returns (expected back)</div>
            <h4 className="fw-bold mb-0 mt-1" style={{ color: '#d97706' }}>{money(totalReturns)}</h4>
          </div></div>
        </div>
      </div>

      <div className="card">
        <div className="table-responsive">
          <table className="table table-hover mb-0 align-middle">
            <thead>
              <tr>
                <th>Date</th><th>Type</th><th>Description</th><th>Location</th>
                <th className="text-end">Amount</th><th>Status</th><th>Receipt</th><th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="text-center py-4"><div className="spinner-border spinner-border-sm" style={{ color: '#ff6b35' }} /></td></tr>
              ) : expenses.length === 0 ? (
                <tr><td colSpan="7" className="text-center py-4 text-muted">No entries yet — register your first expense or loss.</td></tr>
              ) : expenses.map(e => (
                <tr key={e.id}>
                  <td className="text-nowrap">{e.date?.toDate ? e.date.toDate().toLocaleDateString() : '—'}</td>
                  <td>
                    {e.type === 'loss'
                      ? <span className="badge badge-danger">Loss</span>
                      : e.type === 'return'
                        ? <span className="badge badge-warning">Return</span>
                      : e.type === 'ingredient_purchase'
                        ? <span className="badge badge-success">Ingredient purchase</span>
                        : <span className="badge badge-secondary">{e.category || 'Expense'}</span>}
                  </td>
                  <td>
                    <div className="fw-semibold">
                      {e.description || e.ingredientName || e.vendor || '—'}
                      {e.invoiceNumber ? <span className="badge badge-light border ml-1">#{e.invoiceNumber}</span> : null}
                      {Array.isArray(e.lines) && e.lines.length > 0 ? <span className="badge badge-info ml-1">{e.lines.length} lines</span> : null}
                    </div>
                    {e.supplierName && <small className="text-muted d-block"><i className="fas fa-truck mr-1"></i>{e.supplierName}</small>}
                    {e.type === 'loss' && e.reason && <small className="text-muted">{e.reason}{e.ingredientName ? ` · ${e.ingredientName} (−${e.qtyLost} ${e.unit || ''})` : ''}</small>}
                    {e.type === 'ingredient_purchase' && <small className="text-muted">{e.qty} {e.unit}{e.vendor ? ` · ${e.vendor}` : ''}</small>}
                    {e.notes && <small className="text-muted d-block">{e.notes}</small>}
                  </td>
                  <td><small>{locations.find(l => l.id === e.locationId)?.companyName || '—'}</small></td>
                  <td className="text-end fw-bold">{money(e.amount)}</td>
                  <td>
                    {e.status === 'posted'
                      ? <span className="badge badge-dark" title="Posted (accounting-final — read only)">Posted</span>
                      : <span className="badge badge-light border" title="Draft — editable until posted">Draft</span>}
                  </td>
                  <td>{e.receiptUrl ? <a href={e.receiptUrl} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-secondary"><i className="fas fa-receipt"></i></a> : '—'}</td>
                  <td className="text-end text-nowrap">
                    <button className="btn btn-sm btn-outline-secondary mr-1" title="View detail" onClick={() => setViewingEntry(e)}>
                      <i className="fas fa-eye"></i>
                    </button>
                    {e.status !== 'posted' && (<>
                      <button className="btn btn-sm btn-outline-primary mr-1" title="Edit (draft)" onClick={() => { setFormType(e.type === 'loss' ? 'loss' : 'expense'); setEditingEntry(e); setShowForm(true); }}>
                        <i className="fas fa-pen"></i>
                      </button>
                      <button className="btn btn-sm btn-outline-success mr-1" title="Post (locks the entry — accounting final)" onClick={() => handlePost(e)}>
                        <i className="fas fa-lock"></i>
                      </button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(e)}><i className="fas fa-trash"></i></button>
                    </>)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {receiveOffer && (
        <ReceiveMaterialsModal
          offer={receiveOffer}
          ingredients={ingredients}
          products={products}
          locations={locations}
          onClose={() => setReceiveOffer(null)}
          onDone={async () => { setReceiveOffer(null); await load(); }}
        />
      )}

      {viewingEntry && (
        <ExpenseDetailModal
          entry={viewingEntry}
          products={products}
          locations={locations}
          onClose={() => setViewingEntry(null)}
          onChanged={async () => {
            await load();
            // keep the open detail in sync (receipt url / receivedQty)
            const uid = getAuth().currentUser?.uid;
            if (uid) {
              const snap = await getDoc(doc(getFirestore(), 'users', uid, 'expenses', viewingEntry.id));
              if (snap.exists()) setViewingEntry({ id: snap.id, ...snap.data() });
            }
          }}
          onReceive={(exp) => {
            // Offer only what is still pending per line
            const lines = Array.isArray(exp.lines) ? exp.lines : [];
            const ingLines = lines.map((l, lineIndex) => ({ ...l, lineIndex, qty: (l.qty || 0) - (l.receivedQty || 0) }))
              .filter(l => l.ingredientId && l.qty > 0);
            const prodLines = lines.map((l, lineIndex) => ({ ...l, lineIndex, qty: (l.qty || 0) - (l.receivedQty || 0) }))
              .filter(l => {
                if (!l.productId || l.qty <= 0) return false;
                return products.find(x => x.id === l.productId)?.stockScope === 'byLocation';
              });
            setViewingEntry(null);
            setReceiveOffer({ ingredientLines: ingLines, productLines: prodLines, locationId: exp.locationId, expenseId: exp.id });
          }}
        />
      )}

      {showForm && (
        <ExpenseFormModal
          type={formType}
          locations={locations}
          ingredients={ingredients}
          suppliers={suppliers}
          products={products}
          entry={editingEntry}
          onClose={() => { setShowForm(false); setEditingEntry(null); }}
          onSaved={async (receiveOffer) => {
            setShowForm(false);
            setEditingEntry(null);
            await load();
            if (receiveOffer?.ingredientLines?.length > 0) setReceiveOffer(receiveOffer);
          }}
        />
      )}
    </AdminLayout>
  );
};

// ── Ingredient line packaging: qty(packages) × content-per-package × unit ──
// Price is per PACKAGE (matches the supplier invoice); the purchase UOM exists
// ONLY to convert what enters inventory into the use unit. An unconvertible
// pair (e.g. case → g with no known content) blocks the save with a message.
const PURCHASE_UNITS = ['kg', 'g', 'oz', 'lb', 'l', 'ml', 'fl oz', 'gal', 'unit', 'dozen', 'case', 'pack', 'bag', 'box'];

const IngredientPackagingRow = ({ line, ing, idx, form, setForm }) => {
  const upd = (field, value) => {
    const lines = [...form.lines];
    lines[idx] = { ...lines[idx], [field]: value };
    // total stays qty(packages) × price-per-package
    const qty = parseFloat(lines[idx].qty) || 0;
    const up = parseFloat(lines[idx].unitPrice) || 0;
    lines[idx].amount = (qty * up).toFixed(2);
    setForm({ ...form, lines });
  };
  const lineUnit = line.unit || ing.purchaseUnit || 'unit';
  const useUnits = lineUseUnits(line, ing);
  const unconvertible = (parseFloat(line.qty) || 0) > 0 && useUnits == null;
  return (
    <div className="d-flex align-items-center flex-wrap ml-4 mb-2 p-2" style={{ gap: 6, background: '#f8fafc', borderRadius: 8, border: unconvertible ? '1px solid #fecaca' : '1px dashed #e5e7eb' }}>
      <small className="text-muted font-weight-bold">each package contains</small>
      <input
        className="form-control form-control-sm" style={{ width: 74 }} type="number" min="0" step="0.01"
        placeholder="50" value={line.unitsPerPackage ?? ''}
        title="Content per package (e.g. 50 for a 50 lb bag)"
        onChange={(e) => upd('unitsPerPackage', e.target.value)} />
      <select
        className="form-control form-control-sm" style={{ width: 84 }}
        value={lineUnit}
        title="Purchase unit (defaults from the ingredient; only used to convert into inventory)"
        onChange={(e) => upd('unit', e.target.value)}>
        {PURCHASE_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
      </select>
      <small className="text-muted">{ing.purchaseUnit && lineUnit !== ing.purchaseUnit ? `(ingredient's default: ${ing.purchaseUnit})` : ''}</small>
      {useUnits != null && (parseFloat(line.qty) || 0) > 0 && (
        <small className="text-success font-weight-bold ml-auto">
          → {Math.round(useUnits).toLocaleString()} {ing.useUnit || 'unit'} into stock
        </small>
      )}
      {unconvertible && (
        <small className="text-danger font-weight-bold ml-auto">
          ⚠ Can't convert {lineUnit} → {ing.useUnit || 'use unit'} — pick a convertible unit (or fix the ingredient's conversion factor)
        </small>
      )}
    </div>
  );
};

const ExpenseFormModal = ({ type, locations, ingredients, suppliers = [], products = [], entry = null, onClose, onSaved }) => {
  const user = getAuth().currentUser;
  const isLoss = type === 'loss';
  const isReturn = type === 'return';
  const [form, setForm] = useState({
    description: entry?.description || '',
    vendor: entry?.vendor || '',
    category: entry?.category || 'Supplies',
    amount: entry ? (entry.amount ?? '').toString() : '',
    invoiceNumber: entry?.invoiceNumber || '',
    supplierId: entry?.supplierId || '',
    // Invoice lines (header/lines style): when present, the amount is their sum
    lines: Array.isArray(entry?.lines) ? entry.lines.map(l => ({
      description: l.description || '',
      ingredientId: l.ingredientId || '',
      productId: l.productId || '',
      qty: l.qty ?? '',
      unit: l.unit || '',
      unitPrice: l.unitPrice ?? '',
      amount: l.amount ?? '',
    })) : [],
    locationId: entry?.locationId || locations[0]?.id || '',
    ingredientId: entry?.ingredientId || '',
    qtyLost: entry?.qtyLost ?? '',
    reason: entry?.reason || LOSS_REASONS[0],
    returnReason: entry?.returnReason || 'Damaged / defective',
    expectedRefund: entry?.expectedRefund ?? '',
    notes: entry?.notes || '',
    date: entry?.date?.toDate ? entry.date.toDate().toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
  });
  const [existingReceiptUrl] = useState(entry?.receiptUrl || null);
  const [receipt, setReceipt] = useState(null);
  const [saving, setSaving] = useState(false);

  const linesTotal = (form.lines || []).reduce((a, l) => a + (parseFloat(l.amount) || 0), 0);
  const hasLines = (form.lines || []).length > 0;

  const selectedIng = ingredients.find(i => i.id === form.ingredientId);
  const estLossAmount = isLoss && selectedIng?.costPerUseUnit && form.qtyLost
    ? (parseFloat(form.qtyLost) || 0) * selectedIng.costPerUseUnit
    : null;

  const save = async () => {
    // With invoice lines the amount is their SUM (form.amount stays empty —
    // the readOnly input only displays it) — validate the computed total.
    if (hasLines ? linesTotal <= 0 : ((parseFloat(form.amount) || 0) <= 0 && !estLossAmount)) { Swal.fire('Missing', 'Amount is required', 'warning'); return; }
    // An ingredient line whose purchase unit can't convert to its use unit
    // must not save — the stock it delivers would be wrong.
    if (hasLines) {
      for (const line of (form.lines || [])) {
        if (!line.ingredientId) continue;
        const ing = ingredients.find(i => i.id === line.ingredientId);
        if (ing && (parseFloat(line.qty) || 0) > 0 && lineUseUnits(line, ing) == null) {
          setSaving(false);
          Swal.fire(
            "Unit can't convert",
            `"${ing.name}": the purchase unit "${line.unit || ing.purchaseUnit || '?'}" can't convert to "${ing.useUnit || 'use unit'}". Pick a convertible unit on the line (or fix the ingredient's conversion factor).`,
            'warning'
          );
          return;
        }
      }
    }
    setSaving(true);
    try {
      const db = getFirestore();
      let receiptUrl = existingReceiptUrl;
      if (receipt) {
        const storage = getStorage();
        const r = ref(storage, `${user.uid}/expense-receipts/${Date.now()}-${receipt.name}`);
        await uploadBytes(r, receipt);
        receiptUrl = await getDownloadURL(r);
      }
      const amount = hasLines
        ? Math.round(linesTotal * 100) / 100
        : isLoss && estLossAmount != null && !(parseFloat(form.amount) > 0)
          ? Math.round(estLossAmount * 100) / 100
          : parseFloat(form.amount);
      const selectedSupplier = suppliers.find(x => x.id === form.supplierId);
      const expenseRef = entry
        ? doc(db, 'users', user.uid, 'expenses', entry.id)
        : doc(collection(db, 'users', user.uid, 'expenses'));
      const savedLines = (form.lines || [])
        .filter(l => (l.description || '').trim() || l.ingredientId || l.productId)
        .map(l => ({
          description: (l.description || '').trim(),
          ingredientId: l.ingredientId || null,
          productId: l.productId || null,
          ingredientName: ingredients.find(i => i.id === l.ingredientId)?.name || null,
          qty: parseFloat(l.qty) || 0,
          unit: l.unit || ingredients.find(i => i.id === l.ingredientId)?.purchaseUnit || null,
          unitPrice: parseFloat(l.unitPrice) || 0,
          amount: parseFloat(l.amount) || 0,
        }));
      await setDoc(expenseRef, {
        type,
        description: form.description.trim(),
        vendor: form.vendor.trim() || null,
        invoiceNumber: form.invoiceNumber.trim() || null,
        supplierId: form.supplierId || null,
        supplierName: selectedSupplier?.name || null,
        lines: savedLines,
        category: isLoss ? null : form.category,
        amount,
        locationId: form.locationId || null,
        receiptUrl,
        notes: form.notes.trim() || null,
        date: new Date(`${form.date}T12:00:00`),
        // Edits keep the original status (draft stays draft until posted)
        ...(entry ? { updatedAt: serverTimestamp() } : { createdAt: serverTimestamp(), status: 'draft' }),
        ...(isReturn ? {
          returnReason: form.returnReason,
          expectedRefund: parseFloat(form.expectedRefund) > 0 ? parseFloat(form.expectedRefund) : amount,
        } : {}),
        ...(isLoss ? {
          reason: form.reason,
          ingredientId: form.ingredientId || null,
          ingredientName: selectedIng?.name || null,
          qtyLost: parseFloat(form.qtyLost) || 0,
          unit: selectedIng?.useUnit || null,
        } : {}),
      });

      // Final-sale PRODUCT purchase lines raise that product's stock
      // (scope-aware: global pool or the header location's bucket).
      for (const line of (form.lines || [])) {
        if (!line.productId || !(parseFloat(line.qty) > 0)) continue;
        const prod = products.find(x => x.id === line.productId);
        if (!prod) continue;
        const qty = parseFloat(line.qty) || 0;
        const byLocation = prod.stockScope === 'byLocation';
        // Per-location products are received explicitly in the post-save
        // receive step (user picks WHERE). Global products auto-receive here.
        if (byLocation && !isReturn) continue;
        const before = byLocation
          ? Number(prod.stockByLocation?.[form.locationId] || 0)
          : Number(prod.stockQty || 0);
        const after = round4(before + qty);
        const prodRef = doc(db, 'users', user.uid, 'categories', prod.categoryId, 'products', prod.id);
        await setDoc(prodRef, byLocation
          ? { stockByLocation: { [form.locationId]: after }, available: after > 0 ? true : prod.available, updatedAt: serverTimestamp() }
          : { stockQty: after, available: after > 0 ? true : prod.available, updatedAt: serverTimestamp() },
          { merge: true });
        await setDoc(doc(collection(db, 'users', user.uid, 'inventoryMovements')), {
          kind: 'product',
          refId: prod.id,
          name: prod.name || '',
          locationId: byLocation ? form.locationId : null,
          delta: qty,
          before,
          after,
          reason: 'purchase',
          createdAt: serverTimestamp(),
        });
      }

      // RETURN: goods go back to the supplier → stock goes DOWN for every
      // ingredient/product line (movement reason 'return').
      if (isReturn) {
        for (const line of (form.lines || [])) {
          const qty = parseFloat(line.qty) || 0;
          if (qty <= 0) continue;
          if (line.ingredientId) {
            const ing = ingredients.find(x => x.id === line.ingredientId);
            if (!ing) continue;
            const useUnits = qty * (Number(ing.conversionFactor) || 1);
            const before = Number(ing.stockByLocation?.[form.locationId] || 0);
            const after = Math.max(0, round4(before - useUnits));
            await setDoc(doc(db, 'users', user.uid, 'ingredients', ing.id), {
              stockByLocation: { [form.locationId]: after }, updatedAt: serverTimestamp(),
            }, { merge: true });
            await setDoc(doc(collection(db, 'users', user.uid, 'inventoryMovements')), {
              kind: 'ingredient', refId: ing.id, name: ing.name, locationId: form.locationId,
              delta: -useUnits, unit: ing.useUnit || 'unit', before, after,
              reason: 'return', note: form.returnReason, createdAt: serverTimestamp(),
            });
          } else if (line.productId) {
            const prod = products.find(x => x.id === line.productId);
            if (!prod) continue;
            const byLoc = prod.stockScope === 'byLocation';
            const before = byLoc ? Number(prod.stockByLocation?.[form.locationId] || 0) : Number(prod.stockQty || 0);
            const after = Math.max(0, round4(before - qty));
            const prodRef = doc(db, 'users', user.uid, 'categories', prod.categoryId, 'products', prod.id);
            await setDoc(prodRef, byLoc
              ? { stockByLocation: { [form.locationId]: after }, available: after > 0 ? true : prod.available, updatedAt: serverTimestamp() }
              : { stockQty: after, available: after > 0 ? true : prod.available, updatedAt: serverTimestamp() }, { merge: true });
            await setDoc(doc(collection(db, 'users', user.uid, 'inventoryMovements')), {
              kind: 'product', refId: prod.id, name: prod.name || '', locationId: byLoc ? form.locationId : null,
              delta: -qty, before, after, reason: 'return', note: form.returnReason, createdAt: serverTimestamp(),
            });
          }
        }
      }

      // A loss of a raw material also depletes its stock (waste module)
      if (isLoss && selectedIng && (parseFloat(form.qtyLost) || 0) > 0 && form.locationId) {
        const current = Number(selectedIng.stockByLocation?.[form.locationId] || 0);
        await setDoc(doc(collection(db, 'users', user.uid, 'inventoryMovements')), {
          kind: 'ingredient',
          refId: selectedIng.id,
          name: selectedIng.name,
          locationId: form.locationId,
          delta: -(parseFloat(form.qtyLost) || 0),
          unit: selectedIng.useUnit || 'unit',
          before: current,
          after: Math.max(0, round4(current - (parseFloat(form.qtyLost) || 0))),
          reason: 'loss',
          createdAt: serverTimestamp(),
        });
        await setDoc(doc(db, 'users', user.uid, 'ingredients', selectedIng.id), {
          stockByLocation: { [form.locationId]: Math.max(0, round4(current - (parseFloat(form.qtyLost) || 0))) },
          updatedAt: serverTimestamp(),
        }, { merge: true });
      }

      // Ingredient purchase lines → offer to receive them into stock (the
      // missing connection: the invoice alone didn't move inventory).
      if (isReturn) {
        await onSaved(null);
      } else {
        // Build the receive offer from the SAVED lines (indices match the doc,
        // so the receive step can write back per-line receivedQty).
        const ingLines = savedLines
          .map((l, lineIndex) => ({ ...l, lineIndex }))
          .filter(l => l.ingredientId && (l.qty || 0) > 0);
        const prodLines = savedLines
          .map((l, lineIndex) => ({ ...l, lineIndex }))
          .filter(l => {
            if (!l.productId || !(l.qty > 0)) return false;
            const prod = products.find(x => x.id === l.productId);
            return prod?.stockScope === 'byLocation';
          });
        await onSaved((ingLines.length + prodLines.length) > 0
          ? { ingredientLines: ingLines, productLines: prodLines, locationId: form.locationId, expenseId: expenseRef.id }
          : null);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content" style={{ borderRadius: 16 }}>
          <div className="modal-header">
            <h5 className="modal-title font-weight-bold">{entry ? 'Edit' : isLoss ? 'Log a loss / waste' : 'New'}{isLoss ? '' : ' expense'}{entry && isLoss ? ' loss' : ''}</h5>
            <button type="button" className="close" onClick={onClose}><span>&times;</span></button>
          </div>
          <div className="modal-body">
            <div className="row">
              <div className="col-md-8 form-group">
                <label className="font-weight-bold">{isLoss ? 'What was lost' : 'Description'} *</label>
                <input className="form-control" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={isLoss ? 'e.g. 2 trays of pupusas burned' : 'e.g. Monthly rent, gloves supplier…'} />
              </div>
              <div className="col-md-4 form-group">
                <label className="font-weight-bold">Date</label>
                <input type="date" className="form-control" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
            </div>
            {/* Invoice-style fields (expenses as invoices) */}
            <div className="row">
              <div className="col-md-4 form-group">
                <label className="font-weight-bold">Invoice #</label>
                <input className="form-control" value={form.invoiceNumber} onChange={(e) => setForm({ ...form, invoiceNumber: e.target.value })} placeholder="optional" />
              </div>
              <div className="col-md-8 form-group">
                <label className="font-weight-bold">Supplier</label>
                <Select
                  classNamePrefix="ff-select"
                  isClearable
                  isSearchable
                  placeholder="— none / one-off vendor —"
                  options={suppliers.map(sup => ({ value: sup.id, label: sup.name }))}
                  value={suppliers.filter(x => x.id === form.supplierId).map(x => ({ value: x.id, label: x.name }))[0] || null}
                  onChange={(opt) => setForm({ ...form, supplierId: opt?.value || '' })}
                  styles={{ control: (base) => ({ ...base, borderColor: '#ced4da', minHeight: 38, fontSize: 14 }) }}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-md-4 form-group">
                <label className="font-weight-bold">Amount {isLoss && estLossAmount != null ? '(auto-estimated)' : '*'}</label>
                <div className="input-group">
                  <div className="input-group-prepend"><span className="input-group-text">$</span></div>
                  <input
                    type="number" min="0" step="0.01" className="form-control"
                    value={hasLines ? linesTotal.toFixed(2) : form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    placeholder={estLossAmount != null ? estLossAmount.toFixed(2) : '0.00'}
                    readOnly={hasLines}
                  />
                </div>
                {hasLines && <small className="text-muted d-block mt-1">= sum of the lines below</small>}
              </div>
              <div className="col-md-4 form-group">
                <label className="font-weight-bold">Location</label>
                <select className="form-control" value={form.locationId} onChange={(e) => setForm({ ...form, locationId: e.target.value })}>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.companyName || l.locationName}</option>)}
                </select>
              </div>
              {!isLoss && (
                <div className="col-md-4 form-group">
                  <label className="font-weight-bold">Category</label>
                  <select className="form-control" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                    {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              )}
            </div>
            {isReturn && (
              <div className="row">
                <div className="col-md-6 form-group">
                  <label className="font-weight-bold">Return reason</label>
                  <select className="form-control" value={form.returnReason} onChange={(e) => setForm({ ...form, returnReason: e.target.value })}>
                    {['Damaged / defective', 'Wrong item delivered', 'Expired on arrival', 'Over-ordered', 'Other'].map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div className="col-md-6 form-group">
                  <label className="font-weight-bold">Expected refund (from supplier)</label>
                  <div className="input-group">
                    <div className="input-group-prepend"><span className="input-group-text">$</span></div>
                    <input type="number" min="0" step="0.01" className="form-control" value={form.expectedRefund} onChange={(e) => setForm({ ...form, expectedRefund: e.target.value })} placeholder="defaults to line total" />
                  </div>
                </div>
              </div>
            )}
            {isLoss && (
              <div className="row">
                <div className="col-md-4 form-group">
                  <label className="font-weight-bold">Reason</label>
                  <select className="form-control" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}>
                    {LOSS_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div className="col-md-5 form-group">
                  <label className="font-weight-bold">Ingredient affected (optional)</label>
                  <select className="form-control" value={form.ingredientId} onChange={(e) => setForm({ ...form, ingredientId: e.target.value })}>
                    <option value="">— none —</option>
                    {ingredients.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                  </select>
                </div>
                <div className="col-md-3 form-group">
                  <label className="font-weight-bold">Qty lost {selectedIng ? `(${selectedIng.useUnit})` : ''}</label>
                  <input type="number" min="0" step="0.01" className="form-control" value={form.qtyLost} onChange={(e) => setForm({ ...form, qtyLost: e.target.value })} />
                  {estLossAmount != null && <small className="text-muted">est. {money(estLossAmount)} from stock cost</small>}
                </div>
              </div>
            )}
            {/* Invoice lines (header + lines style) */}
            <div className="form-group">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <label className="font-weight-bold mb-0">Lines <small className="text-muted font-weight-normal">(invoice items — ingredient or supply per line)</small></label>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setForm({ ...form, lines: [...(form.lines || []), { description: '', ingredientId: '', qty: '', unit: '', unitPrice: '', amount: '' }] })}>
                  <i className="fas fa-plus mr-1"></i>Add line
                </button>
              </div>
              {(form.lines || []).length > 0 && (
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 8 }}>
                  {(form.lines || []).map((line, idx) => {
                    const ing = ingredients.find(i => i.id === line.ingredientId);
                    return (
                      <div key={idx} className="mb-2">
                      <div className="d-flex align-items-center" style={{ gap: 6 }}>
                        <div style={{ minWidth: 240 }}>
                          <Select
                            classNamePrefix="ff-select"
                            isClearable
                            isSearchable
                            placeholder="— custom / supply —"
                            options={[
                              ...(ingredients.length > 0 ? [{ label: 'Ingredients (purchase unit)', options: ingredients.map(i => ({ value: i.id, label: i.name })) }] : []),
                              ...(products.length > 0 ? [{ label: 'Final-sale products (units)', options: products.map(x => ({ value: `prod:${x.id}`, label: x.name })) }] : []),
                            ]}
                            value={(() => {
                              const v = line.productId ? `prod:${line.productId}` : line.ingredientId;
                              if (!v) return null;
                              const all = [
                                ...ingredients.map(i => ({ value: i.id, label: i.name })),
                                ...products.map(x => ({ value: `prod:${x.id}`, label: x.name })),
                              ];
                              return all.find(o => o.value === v) || null;
                            })()}
                            onChange={(opt) => {
                              const lines = [...form.lines];
                              const v = opt?.value || '';
                              if (v.startsWith('prod:')) {
                                // Final-sale product — bought in UNITS, raises product stock
                                const pick = products.find(x => x.id === v.slice(5));
                                lines[idx] = { ...lines[idx], productId: pick?.id || '', ingredientId: '', description: lines[idx].description || pick?.name || '', unit: 'unit' };
                              } else if (v) {
                                // Ingredient — PURCHASE unit (you buy liters, not ml)
                                const pick = ingredients.find(i => i.id === v);
                                lines[idx] = { ...lines[idx], ingredientId: v, productId: '', description: lines[idx].description || pick?.name || '', unit: pick?.purchaseUnit || '' };
                              } else {
                                lines[idx] = { ...lines[idx], ingredientId: '', productId: '' };
                              }
                              setForm({ ...form, lines });
                            }}
                            styles={{ control: (base) => ({ ...base, borderColor: '#ced4da', minHeight: 31, fontSize: 13 }) }}
                          />
                        </div>
                        <input className="form-control form-control-sm" placeholder="Description" value={line.description}
                          onChange={(e) => { const lines = [...form.lines]; lines[idx] = { ...lines[idx], description: e.target.value }; setForm({ ...form, lines }); }} />
                        <input className="form-control form-control-sm" style={{ width: 70 }} type="number" min="0" step="0.01" placeholder="Qty" value={line.qty}
                          onChange={(e) => {
                            const lines = [...form.lines];
                            const qty = e.target.value;
                            const up = parseFloat(lines[idx].unitPrice) || 0;
                            lines[idx] = { ...lines[idx], qty, amount: ((parseFloat(qty) || 0) * up).toFixed(2) };
                            setForm({ ...form, lines });
                          }} />
                        <small className="text-muted" style={{ minWidth: 26 }}>{ing ? '×' : (line.unit || '')}</small>
                        <input className="form-control form-control-sm" style={{ width: 84 }} type="number" min="0" step="0.0001" placeholder="$/unit" value={line.unitPrice}
                          onChange={(e) => {
                            const lines = [...form.lines];
                            const up = e.target.value;
                            const qty = parseFloat(lines[idx].qty) || 0;
                            lines[idx] = { ...lines[idx], unitPrice: up, amount: (qty * (parseFloat(up) || 0)).toFixed(2) };
                            setForm({ ...form, lines });
                          }} />
                        <input className="form-control form-control-sm font-weight-bold" style={{ width: 90 }} type="number" min="0" step="0.01" placeholder="$" value={line.amount}
                          onChange={(e) => { const lines = [...form.lines]; lines[idx] = { ...lines[idx], amount: e.target.value }; setForm({ ...form, lines }); }} />
                        <button type="button" className="btn btn-sm btn-link text-danger p-0" onClick={() => setForm({ ...form, lines: form.lines.filter((_, i) => i !== idx) })}>
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                      {ing && (
                        <IngredientPackagingRow line={line} ing={ing} idx={idx} form={form} setForm={setForm} />
                      )}
                    </div>
                    );
                  })}
                  <div className="text-right small font-weight-bold pt-1" style={{ borderTop: '1px dashed #e5e7eb' }}>
                    Lines total: {money(linesTotal)}
                  </div>
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="font-weight-bold">Receipt / attachment</label>
              <input type="file" className="form-control-file" accept="image/*,application/pdf" onChange={(e) => setReceipt(e.target.files?.[0] || null)} />
            </div>
            <div className="form-group">
              <label className="font-weight-bold">Notes</label>
              <textarea className="form-control" rows="2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-outline-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="btn btn-primary" style={{ background: '#ff6b35', borderColor: '#ff6b35' }} onClick={save} disabled={saving}>
              {saving ? 'Saving…' : entry ? 'Save changes' : isLoss ? 'Log loss' : 'Save expense'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Read-only detail (posted or not — the full entry) ────────────────────
const ExpenseDetailModal = ({ entry, locations, products, onClose, onReceive, onChanged }) => {
  const isLoss = entry.type === 'loss';
  const isReturn = entry.type === 'return';
  const lines = Array.isArray(entry.lines) ? entry.lines : [];
  const locName = locations.find(l => l.id === entry.locationId)?.companyName || '—';
  const [uploading, setUploading] = useState(false);

  // Lines still pending reception (purchases only): ingredient lines and
  // per-location product lines with remaining qty (global products and
  // returns move stock at save time).
  const receivable = !isLoss && !isReturn
    ? lines.map((l, lineIndex) => ({ ...l, lineIndex })).filter(l => {
        const remaining = (l.qty || 0) - (l.receivedQty || 0);
        if (remaining <= 0) return false;
        if (l.ingredientId) return true;
        if (l.productId) return products.find(x => x.id === l.productId)?.stockScope === 'byLocation';
        return false;
      })
    : [];

  const attachReceipt = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const user = getAuth().currentUser;
      const storage = getStorage();
      const r = ref(storage, `${user.uid}/expense-receipts/${Date.now()}-${file.name}`);
      await uploadBytes(r, file);
      const url = await getDownloadURL(r);
      await setDoc(doc(getFirestore(), 'users', user.uid, 'expenses', entry.id), { receiptUrl: url, updatedAt: serverTimestamp() }, { merge: true });
      await onChanged();
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content" style={{ borderRadius: 16 }}>
          <div className="modal-header">
            <h5 className="modal-title font-weight-bold">
              {isLoss ? 'Loss' : 'Expense'} detail
              {' '}<span className={`badge ${entry.status === 'posted' ? 'bg-dark' : 'bg-light border text-dark'}`}>{entry.status === 'posted' ? 'Posted' : 'Draft'}</span>
              {entry.invoiceNumber ? <span className="badge badge-light border ml-1">#{entry.invoiceNumber}</span> : null}
            </h5>
            <button type="button" className="close" onClick={onClose}><span>&times;</span></button>
          </div>
          <div className="modal-body">
            <div className="row mb-2">
              <div className="col-8">
                <div className="fw-bold" style={{ fontSize: 16 }}>{entry.description || entry.ingredientName || '—'}</div>
                <small className="text-muted">
                  {entry.date?.toDate ? entry.date.toDate().toLocaleDateString() : '—'} · {locName}
                  {entry.supplierName ? ` · ${entry.supplierName}` : ''}{entry.vendor ? ` · ${entry.vendor}` : ''}
                  {entry.category ? ` · ${entry.category}` : ''}
                  {isLoss && entry.reason ? ` · ${entry.reason}` : ''}
                </small>
              </div>
              <div className="col-4 text-end">
                <div style={{ fontSize: 22, fontWeight: 800, color: isLoss ? '#dc2626' : '#111827' }}>{money(entry.amount)}</div>
                <div className="mt-1">
                  {entry.receiptUrl && <a href={entry.receiptUrl} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-secondary mr-1"><i className="fas fa-receipt mr-1"></i>Receipt</a>}
                  <label className="btn btn-sm btn-outline-primary mb-0" style={{ cursor: 'pointer' }}>
                    <i className={`fas ${uploading ? 'fa-spinner fa-spin' : entry.receiptUrl ? 'fa-sync-alt' : 'fa-paperclip'} mr-1`}></i>
                    {uploading ? 'Uploading…' : entry.receiptUrl ? 'Replace' : 'Attach receipt'}
                    <input type="file" accept="image/*,application/pdf" style={{ display: 'none' }} disabled={uploading}
                      onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) attachReceipt(f); }} />
                  </label>
                </div>
              </div>
            </div>

            {lines.length > 0 && (
              <table className="table table-sm" style={{ fontSize: 13 }}>
                <thead>
                  <tr><th>Item</th><th className="text-end">Qty</th><th className="text-end">Unit price</th><th className="text-end">Amount</th></tr>
                </thead>
                <tbody>
                  {lines.map((l, i) => (
                    <tr key={i}>
                      <td>
                        {l.description || l.ingredientName || '—'}
                        {l.unit ? <small className="text-muted"> ({l.unit})</small> : null}
                        {(l.receivedQty || 0) > 0 && <span className="badge badge-success ml-1">received {l.receivedQty}</span>}
                      </td>
                      <td className="text-end">{l.qty || 0}</td>
                      <td className="text-end">{money(l.unitPrice)}</td>
                      <td className="text-end fw-bold">{money(l.amount)}</td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan="3" className="text-end fw-bold" style={{ borderTop: '2px solid #1f2937' }}>Total</td>
                    <td className="text-end fw-bold" style={{ borderTop: '2px solid #1f2937' }}>{money(lines.reduce((a, l) => a + (l.amount || 0), 0))}</td>
                  </tr>
                </tbody>
              </table>
            )}
            {isLoss && entry.qtyLost > 0 && (
              <div className="alert alert-light border small">
                Ingredient depleted: <strong>{entry.ingredientName}</strong> −{entry.qtyLost} {entry.unit || ''}
              </div>
            )}
            {entry.notes && <div className="text-muted small mt-2"><strong>Notes:</strong> {entry.notes}</div>}
          </div>
          <div className="modal-footer d-flex justify-content-between">
            <div>
              {receivable.length > 0 && (
                <button className="btn btn-success" onClick={() => onReceive(entry)}>
                  <i className="fas fa-truck-loading mr-1"></i>
                  Receive into stock ({receivable.length} item{receivable.length > 1 ? 's' : ''} pending)
                </button>
              )}
              {entry.inventoryLastReceivedAt && receivable.length === 0 && !isLoss && !isReturn && lines.some(l => l.ingredientId || l.productId) && (
                <span className="badge badge-success p-2"><i className="fas fa-check mr-1"></i>All received</span>
              )}
            </div>
            <button className="btn btn-outline-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Post-save step: receive the invoice's ingredient lines into stock ──────
const ReceiveMaterialsModal = ({ offer, ingredients, products, locations, onClose, onDone }) => {
  const user = getAuth().currentUser;
  const items = [
    ...(offer.ingredientLines || []).map(l => ({ kind: 'ingredient', line: l })),
    ...(offer.productLines || []).map(l => ({ kind: 'product', line: l })),
  ];
  const [checked, setChecked] = useState(() => Object.fromEntries(items.map((_, i) => [i, true])));
  // Per-item split receiving: [{ locationId, qty }] — starts as full qty at
  // the invoice location; the user can split an item across locations.
  const [splits, setSplits] = useState(() => Object.fromEntries(items.map((it, i) => {
    const qty = parseFloat(it.line.qty) || 0;
    return [i, [{ locationId: offer.locationId || locations[0]?.id || '', qty }]];
  })));
  const [busy, setBusy] = useState(false);

  const apply = async () => {
    setBusy(true);
    try {
      const db = getFirestore();
      for (const [iStr, on] of Object.entries(checked)) {
        if (!on) continue;
        const idx = Number(iStr);
        const { kind, line } = items[idx];
        const rows = (splits[idx] || []).filter(r => (parseFloat(r.qty) || 0) > 0 && r.locationId);
        for (const row of rows) {
          const splitQty = parseFloat(row.qty) || 0;
          if (kind === 'ingredient') {
            const ing = ingredients.find(i => i.id === line.ingredientId);
            if (!ing) continue;
            // packages × content-per-package × unit conversion
            const useUnits = lineUseUnits({ ...line, qty: splitQty }, ing) ?? splitQty;
            const lineTotal = (parseFloat(line.amount) || 0) * (splitQty / ((parseFloat(line.qty) || 0) || 1)); // prorated cost
            const before = Number(ing.stockByLocation?.[row.locationId] || 0);
            const after = round4(before + useUnits);
            const currentCost = Number(ing.costPerUseUnit) || 0;
            // Weighted-average cost (same rule as the Receive flow)
            const newAvg = after > 0 ? ((before * currentCost) + lineTotal) / after : currentCost;
            await setDoc(doc(db, 'users', user.uid, 'ingredients', ing.id), {
              stockByLocation: { [row.locationId]: after },
              costPerUseUnit: newAvg,
              lastPurchaseAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            }, { merge: true });
            await setDoc(doc(collection(db, 'users', user.uid, 'inventoryMovements')), {
              kind: 'ingredient', refId: ing.id, name: ing.name, locationId: row.locationId,
              delta: useUnits, unit: ing.useUnit || 'unit', before, after,
              reason: 'purchase', note: 'from expense invoice', createdAt: serverTimestamp(),
            });
          } else {
            const prod = products.find(x => x.id === line.productId);
            if (!prod) continue;
            const before = Number(prod.stockByLocation?.[row.locationId] || 0);
            const after = round4(before + splitQty);
            await setDoc(doc(db, 'users', user.uid, 'categories', prod.categoryId, 'products', prod.id), {
              stockByLocation: { [row.locationId]: after },
              available: after > 0 ? true : prod.available,
              updatedAt: serverTimestamp(),
            }, { merge: true });
            await setDoc(doc(collection(db, 'users', user.uid, 'inventoryMovements')), {
              kind: 'product', refId: prod.id, name: prod.name || '', locationId: row.locationId,
              delta: splitQty, before, after, reason: 'purchase', note: 'from expense invoice', createdAt: serverTimestamp(),
            });
          }
        }
      }
      // Write back per-line receivedQty so the invoice shows what's already
      // in stock and the detail view only offers the remaining quantities.
      if (offer.expenseId) {
        const db2 = getFirestore();
        const expRef = doc(db2, 'users', user.uid, 'expenses', offer.expenseId);
        const expSnap = await getDoc(expRef);
        if (expSnap.exists()) {
          const docLines = Array.isArray(expSnap.data().lines) ? [...expSnap.data().lines] : [];
          for (const [iStr, on] of Object.entries(checked)) {
            if (!on) continue;
            const { line } = items[Number(iStr)];
            if (line.lineIndex == null || !docLines[line.lineIndex]) continue;
            const got = (splits[Number(iStr)] || []).reduce((a, r) => a + (parseFloat(r.qty) || 0), 0);
            docLines[line.lineIndex] = { ...docLines[line.lineIndex], receivedQty: (docLines[line.lineIndex].receivedQty || 0) + got };
          }
          await setDoc(expRef, { lines: docLines, inventoryLastReceivedAt: serverTimestamp() }, { merge: true });
        }
      }
      await onDone();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1070 }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content" style={{ borderRadius: 16 }}>
          <div className="modal-header">
            <h5 className="modal-title font-weight-bold"><i className="fas fa-truck-loading mr-2" style={{ color: '#16a34a' }}></i>Receive materials into stock?</h5>
            <button type="button" className="close" onClick={onClose}><span>&times;</span></button>
          </div>
          <div className="modal-body">
            <p className="text-muted small">Distribute each item across locations — the split quantities must add up to the purchased qty.</p>
            {items.map(({ kind, line }, i) => {
              const ing = kind === 'ingredient' ? ingredients.find(x => x.id === line.ingredientId) : null;
              const prod = kind === 'product' ? products.find(x => x.id === line.productId) : null;
              const purchaseQty = parseFloat(line.qty) || 0;
              const factor = kind === 'ingredient' ? (Number(ing?.conversionFactor) || 1) : 1;
              const unitLabel = kind === 'ingredient' ? (ing?.useUnit || 'unit') : 'units';
              const buyUnit = line.unit || '';
              const rows = splits[i] || [];
              const allocated = rows.reduce((a, r) => a + (parseFloat(r.qty) || 0), 0);
              const remaining = purchaseQty - allocated;
              const balanced = Math.abs(remaining) < 1e-9;
              return (
                <div className="py-2" style={{ borderBottom: '1px dashed #f0f0f0' }} key={i}>
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="custom-control custom-checkbox">
                      <input type="checkbox" className="custom-control-input" id={`recv-${i}`} checked={checked[i] !== false} onChange={(e) => setChecked({ ...checked, [i]: e.target.checked })} />
                      <label className="custom-control-label" htmlFor={`recv-${i}`}>
                        <strong>{ing?.name || prod?.name || line.description}</strong>
                        <small className="text-muted d-block">
                          purchased: {purchaseQty} {buyUnit}{kind === 'ingredient' ? ` (= ${(lineUseUnits({ ...line, qty: purchaseQty }, ingredients.find(i2 => i2.id === line.ingredientId)) ?? purchaseQty).toLocaleString()} ${unitLabel})` : ''}
                          <span className="badge badge-light ml-1">{kind}</span>
                        </small>
                      </label>
                    </div>
                    {(checked[i] !== false) && (
                      <span className={`badge ${balanced ? 'badge-success' : 'badge-warning'}`}>
                        {balanced ? 'balanced' : `${remaining.toLocaleString()} ${buyUnit || 'units'} unassigned`}
                      </span>
                    )}
                  </div>
                  {(checked[i] !== false) && rows.map((row, ri) => (
                    <div className="d-flex align-items-center mt-1 ml-4" key={ri}>
                      <select
                        className="form-control form-control-sm mr-2"
                        style={{ maxWidth: 200 }}
                        value={row.locationId}
                        onChange={(e) => {
                          const next = { ...splits, [i]: rows.map((r, j) => j === ri ? { ...r, locationId: e.target.value } : r) };
                          setSplits(next);
                        }}
                      >
                        {locations.map(l => <option key={l.id} value={l.id}>{l.companyName}</option>)}
                      </select>
                      <input
                        type="number" min="0" step="any"
                        className="form-control form-control-sm mr-2"
                        style={{ width: 110 }}
                        value={row.qty}
                        onChange={(e) => {
                          const next = { ...splits, [i]: rows.map((r, j) => j === ri ? { ...r, qty: e.target.value } : r) };
                          setSplits(next);
                        }}
                      />
                      <small className="text-muted mr-2">
                        {buyUnit || 'units'}{kind === 'ingredient' ? ` → +${((parseFloat(row.qty) || 0) * factor).toLocaleString()} ${unitLabel}` : ''}
                      </small>
                      {rows.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-link btn-sm text-danger p-0 mr-2"
                          onClick={() => setSplits({ ...splits, [i]: rows.filter((_, j) => j !== ri) })}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                      {ri === rows.length - 1 && locations.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0"
                          onClick={() => setSplits({ ...splits, [i]: [...rows, { locationId: locations.find(l => !rows.some(r => r.locationId === l.id))?.id || locations[0].id, qty: '' }] })}
                        >
                          <i className="fas fa-code-branch mr-1"></i>split
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
          <div className="modal-footer">
            <button className="btn btn-outline-secondary" onClick={onClose} disabled={busy}>Skip (invoice only)</button>
            <button className="btn btn-success" onClick={apply} disabled={busy || !Object.values(checked).some(Boolean) || items.some((it, i) => {
              if (checked[i] === false) return false;
              const purchaseQty = parseFloat(it.line.qty) || 0;
              const allocated = (splits[i] || []).reduce((a, r) => a + (parseFloat(r.qty) || 0), 0);
              return Math.abs(purchaseQty - allocated) >= 1e-9;
            })}>
              {busy ? 'Receiving…' : 'Receive into stock'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpensesPage;
